import { createFileRoute } from '@tanstack/react-router';
import { CaseStudyLayout, type CaseStudyContent } from '../components/CaseStudyLayout.js';
import { RoomMatchMockup } from '../components/visuals/RoomMatchMockup.js';
import { IconClinic } from '../components/visuals/IndustryIcons.js';
import { usePageTitle } from '../components/usePageTitle.js';

export const Route = createFileRoute('/for-accountants')({
  component: AccountantsPage,
});

function AccountantsPage() {
  usePageTitle('Bookings for accountants & tax agents');
  return <CaseStudyLayout content={content} />;
}

const content: CaseStudyContent = {
  eyebrow: 'For accountants & tax agents',
  icon: IconClinic,
  headline: 'Tax season without the diary chaos.',
  headlineEmphasis: 'diary chaos',
  subhead:
    'Bookaburra remembers when each client is due — annual return, quarterly BAS, EOFY check-in — and lets them re-book themselves from the email reminder. No spreadsheet, no phone tag.',
  Visual: RoomMatchMockup,
  business: {
    name: 'Linfield Accounting',
    location: 'Sydney North Shore · 3 accountants · 2 meeting rooms (1 video-equipped)',
    summary:
      'A fictional small accounting practice on the North Shore. Three accountants, two meeting rooms — one with a webcam and good lighting for online clients. Mix of 30-minute BAS check-ins, 60-minute annual returns, and 90-minute new-client onboarding. July through October is the peak; the rest of the year runs on a quarterly BAS rhythm.',
  },
  dayTimeline: [
    {
      time: '9:00',
      event:
        'Day starts. Three BAS check-ins booked back-to-back in the standard room; the video-equipped room is held for an 11am online client.',
      bookaburraResponse:
        'Nothing to do. Each booking matched the room feature it actually needed.',
    },
    {
      time: '9:45',
      event:
        'A new client books a 90-minute onboarding online and picks the next available morning.',
      bookaburraResponse:
        'Only offers slots with a full 90 minutes free in a meeting room. The half-hour gap between BAS check-ins is never shown.',
    },
    {
      time: '10:50',
      event:
        'The 11am online client clicks through. The standard room is free, but the meeting needs a webcam.',
      bookaburraResponse:
        'That booking only ever landed in the video-equipped room. No mid-call room-swap, no apology.',
    },
    {
      time: '11:30',
      event:
        'BAS check-in client opens his confirmation email — it lists the three documents he needs to bring.',
      bookaburraResponse:
        'Service-specific prep instructions render from the saved template and went out at booking time.',
    },
    {
      time: '13:20',
      event:
        'Sunday admin task surfaces: 14 clients on the follow-up list are overdue for their annual return.',
      bookaburraResponse:
        'Follow-up list groups them by last-visit cadence. Reception sends a single batch of "book your return" magic links — clients self-serve from there.',
    },
    {
      time: '15:10',
      event:
        'A long-standing client books her own annual return for next Tuesday from the magic link in this morning’s reminder.',
      bookaburraResponse:
        'No phone tag with reception. Slot respects the 60-minute service duration; the partner’s calendar feed updates automatically.',
    },
  ],
  pains: [
    {
      quote:
        'Through tax season the phones ring constantly, and the team is typing appointment times into a paper diary. Mistakes happen.',
      explanation:
        'A shared online calendar removes the re-typing step and the double-bookings that come with it.',
    },
    {
      quote:
        "Some of our long-standing clients are a year overdue on their return. Without a follow-up list it’s whoever rings us next, not who’s actually due.",
      explanation:
        'Bookaburra tracks each client’s last visit per service and surfaces the ones who have drifted past their cadence.',
    },
    {
      quote:
        'An online client got booked into the room without the webcam. We had to swap rooms mid-call.',
      explanation:
        'Resource matching prevents the booking before the awkward swap.',
    },
  ],
  solutions: [
    {
      title: 'Follow-up list of overdue clients',
      body: (
        <>
          Bookaburra shows which clients are overdue for their annual return or
          quarterly BAS based on each service&rsquo;s recommended frequency —
          so the team works from a list, not from memory.
        </>
      ),
    },
    {
      title: 'Self-serve booking from the reminder',
      body: (
        <>
          Clients book themselves directly from the email reminder via a magic
          link. No phone tag with reception, no back-and-forth to find a time.
        </>
      ),
    },
    {
      title: 'Match online clients with the video room',
      body: (
        <>
          Services that need the video-equipped room only offer slots where that
          room is free. The other room is left available for in-person work.
        </>
      ),
    },
    {
      title: 'Slots that respect service duration',
      body: (
        <>
          A 90-minute new-client onboarding cannot be squeezed into a 30-minute
          gap between BAS check-ins. Slots use the full service duration and
          your buffers.
        </>
      ),
    },
  ],
  industrySlug: 'accountants',
};
